import config from '../config';
import selectedEnvironment from '../env';

const baseURL = config[selectedEnvironment].API_URL;
const AccountCards_API_URL = `${baseURL}/AccountCards`;

/**
 * Get preview of CardId changes when reordering by PalCode prefix (normalization).
 * @param {string} caseId
 * @param {string} auth - Bearer token
 * @returns {Promise<{ changes: Array<{ oldCardId: number, newCardId: number, name: string, palCode: string }> }>}
 */
export const getReorderByPrefixPreview = async (caseId, auth) => {
    const response = await fetch(
        `${AccountCards_API_URL}/ReorderByPrefixPreview?caseId=${encodeURIComponent(caseId)}`,
        {
            method: 'GET',
            headers: {
                Authorization: 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        }
    );
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to get reorder preview');
    }
    return response.json();
};

/**
 * Commit CardId reorder by prefix. Updates all documents with new CardIds.
 * @param {string} caseId
 * @param {Array<{ oldCardId: number, newCardId: number, name?: string, palCode?: string }> | null} migrations - If null/empty, server computes from preview and applies
 * @param {string} auth - Bearer token
 * @returns {Promise<void>}
 */
export const reorderByPrefixCommit = async (caseId, migrations, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/ReorderByPrefixCommit`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ CaseId: caseId, Migrations: migrations || null }),
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to commit reorder by prefix');
    }
};

/**
 * Set a single account card editable/non-editable.
 * Backend expects CaseId, CardId (string), IsEditable.
 * @param {string} caseId
 * @param {number|string} cardId
 * @param {boolean} isEditable
 * @param {string} auth - Bearer token
 * @returns {Promise<Response>}
 */
export const setAccountCardEditable = async (caseId, cardId, isEditable, auth) => {
    const cardIdStr = cardId == null ? '' : String(cardId);
    const response = await fetch(`${AccountCards_API_URL}/SetEditable`, {
        method: 'PUT',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            CaseId: caseId,
            CardId: cardIdStr,
            IsEditable: isEditable,
        }),
    });
    return response;
};

/**
 * Set multiple account cards editable in one request (bulk endpoint).
 * @param {string} caseId
 * @param {Array<number|string>} cardIds
 * @param {boolean} isEditable
 * @param {string} auth - Bearer token
 * @returns {Promise<{ successCount: number, errors: string[] }>}
 */
export const setAccountCardsEditableBulk = async (caseId, cardIds, isEditable, auth) => {
    const cardIdsStr = (cardIds || [])
        .map((id) => (id != null ? String(id) : ''))
        .filter(Boolean);
    if (cardIdsStr.length === 0) {
        return { successCount: 0, errors: [] };
    }
    const response = await fetch(`${AccountCards_API_URL}/SetEditableBulk`, {
        method: 'PUT',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            CaseId: caseId,
            CardIds: cardIdsStr,
            IsEditable: isEditable,
        }),
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to set editable in bulk');
    }
    return { successCount: cardIdsStr.length, errors: [] };
};

/**
 * Get next suggested CardId for a case and optional PalCode (uses prefix range if configured).
 * @param {string} caseId
 * @param {string} [palCodeId] - optional PalCode; if provided and has prefix, returns next ID in that range
 * @param {string} auth - Bearer token
 * @returns {Promise<{ cardId: string }>}
 */
export const getNextCardId = async (caseId, palCodeId, auth) => {
    const params = new URLSearchParams({ caseId });
    if (palCodeId) params.set('palCodeId', palCodeId);
    const response = await fetch(`${AccountCards_API_URL}/NextCardId?${params}`, {
        method: 'GET',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to get next card ID');
    }
    return response.json();
};

/**
 * Swap two account cards: their data and all related documents are swapped (CardIds stay the same).
 * @param {string} caseId
 * @param {string} cardId1
 * @param {string} cardId2
 * @param {string} auth - Bearer token
 * @returns {Promise<{ success: boolean }>}
 */
export const swapAccountCards = async (caseId, cardId1, cardId2, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/SwapAccountCards`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            CaseId: caseId,
            CardId1: String(cardId1),
            CardId2: String(cardId2),
        }),
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to swap account cards');
    }
    return response.json();
};

export default {
    getReorderByPrefixPreview,
    reorderByPrefixCommit,
    setAccountCardEditable,
    setAccountCardsEditableBulk,
    getNextCardId,
    swapAccountCards,
};
